import { Formik, FormikProps } from 'formik';
import React, { useRef } from 'react';
import { isUndefined } from 'lodash';
import { Trans, useTranslation } from 'react-i18next';
import useGetTenantGroupTenants from 'Hooks/query-hooks/tenant-groups/useGetTenantGroupTenants';
import useRemoveTenantsFromTenantGroup from 'Hooks/query-hooks/tenant-groups/useRemoveTenantsFromTenantGroup';
import useReduxModal from 'Hooks/reduxHelperHooks/useReduxModal';
import errorController from 'Components/ErrorScreens';
import RouteLoading from 'Components/RouteLoading';
import { EventNames, EventTypes, trackEvent } from '@brighterion/insights';
import TenantCheckboxTable from '../TenantCheckboxTable';

import styles from './TenantGroupTenants.css';

type TenantGroupTenantsProps = {
    tenantGroupId: string;
    tenantGroupName?: string;
};

const TenantGroupTenants = ({ tenantGroupId, tenantGroupName }: TenantGroupTenantsProps) => {
    const tenantGroupTenantsAPI = useGetTenantGroupTenants(tenantGroupId);
    const { hasError, errorCmp } = errorController([tenantGroupTenantsAPI.error]);
    const formRef = useRef<FormikProps<TenantCheckboxTableForm>>(null);
    const { addTechnicalErrorModal, clearAll } = useReduxModal();
    const { t } = useTranslation();

    const mutation = useRemoveTenantsFromTenantGroup({
        formRef,
        tenantGroupId,
        tenantGroupName
    });

    const handleSubmit = (formValues: TenantCheckboxTableForm) => {
        const tenantIds = formValues.rows.filter((r) => r.isSelected).map((r) => r.tenant);
        const count = tenantIds.length;

        addTechnicalErrorModal('', t('Remove Tenants from Group'), clearAll, {
            status: 'warning',
            content: (
                <>
                    <Trans i18nKey='removeTenantsFromTenantGroupWarningMsg' count={count}>
                        Are you sure you want remove <strong>{{ count }} tenants</strong> from the {{ tenantGroupName }}
                        ?
                    </Trans>
                    <div style={{ marginTop: '24px' }}>{t('This action cannot be undone')}</div>
                </>
            ),
            onActionText: t('Remove Tenants'),
            onAction: () => {
                mutation.mutate({
                    tenantKeys: tenantIds
                });
                trackEvent({
                    eventType: EventTypes.track,
                    eventName: EventNames.click,
                    payload: {
                        clickIdentifier: 'Tenant Groups - Remove Tenants from Group',
                        text: 'Remove Tenants from Group'
                    }
                });
                clearAll();
            },
            cancelText: t('Close')
        });
    };

    if (hasError || isUndefined(tenantGroupTenantsAPI.data?.modifiedTenants)) {
        return <div className={styles.tableContainer}>{errorCmp}</div>;
    }

    if (tenantGroupTenantsAPI.isLoading || tenantGroupTenantsAPI.isFetching) {
        return <RouteLoading />;
    }

    const tableInitialState = {
        rows: tenantGroupTenantsAPI.data?.original.tenants ?? [],
        headerCheckboxSelected: false
    };

    return (
        <div className={styles.tableContainer}>
            <Formik enableReinitialize initialValues={tableInitialState} onSubmit={handleSubmit} innerRef={formRef}>
                <TenantCheckboxTable tableName='removeTenantTable' />
            </Formik>
        </div>
    );
};

export default TenantGroupTenants;
